import {combineReducers} from 'redux';
import {all} from 'redux-saga/effects';
import {merge} from 'lodash';

/* Reducers */
import loadReducer, {generateDefaultInitialLoadState} from '../reducers/loadReducer';
import accountReducer, {generateDefaultInitialAccountState} from '../reducers/accountReducer';
import productReducer, {generateDefaultInitialProductState} from '../reducers/productReducer';
import filterReducer, {generateDefaultInitialFilterState} from '../reducers/filterReducer';
import bannerReducer, {generateDefaultInitialBannerState} from '../reducers/bannerReducer';
import featureToggleReducer, {generateDefaultInitialFeatureToggleState} from '../reducers/featureToggleReducer';
import experimentReducer, {generateDefaultInitialExperimentState} from '../reducers/experimentReducer';
import seoReducer, {generateDefaultInitialSeoState} from '../reducers/seoReducer';
import cartReducer, {generateDefaultInitialCartState} from '../reducers/cartReducer';
import categoryReducer, {generateDefaultInitialCategoryState} from '../reducers/categoryReducer';
import searchReducer, {generateDefaultInitialSearchState} from '../reducers/searchReducer';
import tagReducer, {generateDefaultInitialTagState} from '../reducers/tagReducer';
import errorReducer, {generateDefaultInitialErrorState} from '../reducers/errorReducer';
import pendingReducer, {generateDefaultInitialPendingState} from '../reducers/pendingReducer';
import modalReducer, {generateDefaultInitialModalState} from '../reducers/modalReducer';
import abTestReducer, {generateDefaultInitialAbTestState} from '../reducers/abTestReducer';
import dictionaryReducer, {generateDefaultInitialDictionaryState} from '../reducers/dictionaryReducer';
import voltranReducer, {generateDefaultInitialVoltranState} from '../reducers/voltranReducer';
import homePageComponentsReducer, {generateDefaultInitialHomePageComponentsState} from '../reducers/homePageComponentsReducer';
import campaignReducer, {generateDefaultInitialCampaignState} from '../reducers/campaignReducer';
import landingPageReducer, {generateDefaultInitialLandingPageState} from '../reducers/landingPageReducer';
import staticPageReducer, {generateDefaultInitialStaticPageState} from '../reducers/staticPageReducer';
import userAgentReducer, {generateDefaultUserAgentState} from '../reducers/userAgentReducer';
import footerReducer, {generateDefaultFooterState} from '../reducers/footerReducer';
import brandReducer, {generateDefaultInitialBrandState} from '../reducers/brandReducer';
import cmsComponentReducer, {generateDefaultInitialCmsComponentsState} from '../reducers/cmsComponentReducer';
import customerReducer, {generateDefaultInitialCustomerState} from '../reducers/customerReducer';
import authorReducer, {generateDefaultInitialAuthorState} from '../reducers/authorReducer';
import brandsReducer, {generateDefaultInitialBrandsState} from '../reducers/brandsReducer';
import googleAdUnitsReducer, {generateDefaultInitialGoogleAdUnitsState} from '../reducers/googleAdUnitReducer';
import merchantReducer , {generateDefaultInitialMerchantState} from '../reducers/merchantReducer';
import hydraHomePageComponentsReducer, {generateDefaultInitialHydraHomePageComponentsState} from '../reducers/hydraHomePageComponentsReducer';
import shoppingAssistantReducer , {generateDefaultInitialShoppingAssistantState} from '../reducers/shoppingAssistantReducer';
import merchantsReducer, {generateDefaultInitialMerchantsState} from '../reducers/merchantsReducer';

/* Sagas */
import productSagasFactory from '../sagas/productSagas';
import bannerSagasFactory from '../sagas/bannerSagas';
import featureToggleSagasFactory from '../sagas/featureToggleSagas';
import experimentSagasFactory from '../sagas/experimentSagas';
import filterSagasFactory from '../sagas/filterSagas';
import seoSagasFactory from '../sagas/seoSagas';
import accountSagasFactory from '../sagas/accountSagas';
import cartSagasFactory from '../sagas/cartSagas';
import categorySagasFactory from '../sagas/categorySagas';
import searchSagasFactory from '../sagas/searchSagas';
import tagSagasFactory from '../sagas/tagSagas';
import merchantSagasFactory from '../sagas/merchantSagas';
import analyticsSagasFactory from '../sagas/analyticsSagas';
import abTestSagasFactory from '../sagas/abTestSagas';
import dictionarySagasFactory from '../sagas/dictionarySagas';
import voltranSagasFactory from '../sagas/voltranSagas';
import homePageComponentsSagasFactory from '../sagas/homePageComponentsSaga';
import campaignSagasFactory from '../sagas/campaignSagas';
import landingPageSagasFactory from '../sagas/landingPageSagas';
import staticPageSagasFactory from '../sagas/staticPageSagas';
import brandSagasFactory from '../sagas/brandSagas';
import cmsComponentSagasFactory from '../sagas/cmsComponentSaga';
import customerSagasFactory from '../sagas/customerSagas';
import authorSagasFactory from '../sagas/authorSagas';
import brandsSagasFactory from '../sagas/brandsSagas';
import googleAdUnitSagasFactory from '../sagas/googleAdUnitSagas';
import hydraHomePageComponentsSagasFactory from '../sagas/hydraHomePageComponentsSaga';
import shoppingAssistantSagasFactory from '../sagas/shoppingAssistantSagas';
import footerSagasFactory from '../sagas/footerSagas';
import merchantsSagasFactory from '../sagas/merchantsSagas';

function generateInitialReduxStoreState(customInitialState = {}) {
  const initialState = {
    accountState: merge(
      generateDefaultInitialAccountState(),
      customInitialState.accountState,
    ),
    productState: merge(
      generateDefaultInitialProductState(),
      customInitialState.productState,
    ),
    filterState: merge(
      generateDefaultInitialFilterState(),
      customInitialState.filterState,
    ),
    bannerState: merge(
      generateDefaultInitialBannerState(),
      customInitialState.bannerState,
    ),
    featureToggleState: merge(
      generateDefaultInitialFeatureToggleState(),
      customInitialState.featureToggleState,
    ),
    experimentState: merge(
      generateDefaultInitialExperimentState(),
      customInitialState.experimentState,
    ),
    seoState: merge(
      generateDefaultInitialSeoState(),
      customInitialState.seoState,
    ),
    cartState: merge(
      generateDefaultInitialCartState(),
      customInitialState.cartState,
    ),
    categoryState: merge(
      generateDefaultInitialCategoryState(),
      customInitialState.categoryState,
    ),
    searchState: merge(
      generateDefaultInitialSearchState(),
      customInitialState.searchState,
    ),
    tagState: merge(
      generateDefaultInitialTagState(),
      customInitialState.tagState,
    ),
    merchantState: merge(
      generateDefaultInitialMerchantState(),
      customInitialState.merchantState,
    ),
    errorState: merge(
      generateDefaultInitialErrorState(),
      customInitialState.errorState,
    ),
    pendingState: merge(
      generateDefaultInitialPendingState(),
      customInitialState.pendingState,
    ),
    modalState: merge(
      generateDefaultInitialModalState(),
      customInitialState.modalState,
    ),
    loadState: merge(
      generateDefaultInitialLoadState(),
      customInitialState.loadState,
    ),
    abTestState: merge(
      generateDefaultInitialAbTestState(),
      customInitialState.abTestState,
    ),
    dictionaryState: merge(
      generateDefaultInitialDictionaryState(),
      customInitialState.dictionaryState,
    ),
    voltranState: merge(
      generateDefaultInitialVoltranState(),
      customInitialState.voltranState,
    ),
    homePageComponentsState: merge(
      generateDefaultInitialHomePageComponentsState(),
      customInitialState.homePageComponentsState,
    ),
    hydraHomePageComponentsState: merge(
      generateDefaultInitialHydraHomePageComponentsState(),
      customInitialState.hydraHomePageComponentsState,
    ),
    campaignState: merge(
      generateDefaultInitialCampaignState(),
      customInitialState.campaignState,
    ),
    landingPageState: merge(
      generateDefaultInitialLandingPageState(),
      customInitialState.landingPageState,
    ),
    staticPageState: merge(
      generateDefaultInitialStaticPageState(),
      customInitialState.staticPageState,
    ),
    userAgentState: merge(
      generateDefaultUserAgentState(),
      customInitialState.userAgentState,
    ),
    footerState: merge(
      generateDefaultFooterState(),
      customInitialState.footerState,
    ),
    brandState: merge(
      generateDefaultInitialBrandState(),
      customInitialState.brandState,
    ),
    cmsComponentState: merge(
      generateDefaultInitialCmsComponentsState(),
      customInitialState.cmsComponentState,
    ),
    customerState: merge(
      generateDefaultInitialCustomerState(),
      customInitialState.customerState,
    ),
    authorState: merge(
      generateDefaultInitialAuthorState(),
      customInitialState.authorState,
    ),
    brandsState: merge(
      generateDefaultInitialBrandsState(),
      customInitialState.brandsState,
    ),
    googleAdUnitsState: merge(
      generateDefaultInitialGoogleAdUnitsState(),
      customInitialState.googleAdUnitsState
    ),
    shoppingAssistantState: merge(
      generateDefaultInitialShoppingAssistantState(),
      customInitialState.shoppingAssistantState,
    ),
    merchantsState: merge(
      generateDefaultInitialMerchantsState(),
      customInitialState.merchantsState,
    ),
  };

  return initialState;
}

function generateRootReducer() {
  const reducers = {
    accountState: accountReducer,
    productState: productReducer,
    filterState: filterReducer,
    bannerState: bannerReducer,
    featureToggleState: featureToggleReducer,
    experimentState: experimentReducer,
    seoState: seoReducer,
    cartState: cartReducer,
    categoryState: categoryReducer,
    searchState: searchReducer,
    tagState: tagReducer,
    merchantState:merchantReducer,
    errorState: errorReducer,
    pendingState: pendingReducer,
    modalState: modalReducer,
    loadState: loadReducer,
    abTestState: abTestReducer,
    dictionaryState: dictionaryReducer,
    voltranState: voltranReducer,
    homePageComponentsState: homePageComponentsReducer,
    hydraHomePageComponentsState: hydraHomePageComponentsReducer,
    campaignState: campaignReducer,
    landingPageState: landingPageReducer,
    userAgentState: userAgentReducer,
    footerState: footerReducer,
    staticPageState: staticPageReducer,
    brandState: brandReducer,
    cmsComponentState: cmsComponentReducer,
    customerState: customerReducer,
    authorState: authorReducer,
    brandsState: brandsReducer,
    googleAdUnitsState: googleAdUnitsReducer,
    shoppingAssistantState: shoppingAssistantReducer,
    merchantsState: merchantsReducer,
  };

  return combineReducers(reducers);
}

function generateUniversalSagas(...params) {
  return [
    ...productSagasFactory(...params),
    ...bannerSagasFactory(...params),
    ...featureToggleSagasFactory(...params),
    ...experimentSagasFactory(...params),
    ...filterSagasFactory(...params),
    ...seoSagasFactory(...params),
    ...accountSagasFactory(...params),
    ...cartSagasFactory(...params),
    ...categorySagasFactory(...params),
    ...searchSagasFactory(...params),
    ...tagSagasFactory(...params),
    ...abTestSagasFactory(...params),
    ...dictionarySagasFactory(...params),
    ...voltranSagasFactory(...params),
    ...homePageComponentsSagasFactory(...params),
    ...hydraHomePageComponentsSagasFactory(...params),
    ...campaignSagasFactory(...params),
    ...landingPageSagasFactory(...params),
    ...staticPageSagasFactory(...params),
    ...brandSagasFactory(...params),
    ...cmsComponentSagasFactory(...params),
    ...customerSagasFactory(...params),
    ...authorSagasFactory(...params),
    ...brandsSagasFactory(...params),
    ...merchantsSagasFactory(...params),
    ...googleAdUnitSagasFactory(...params),
    ...merchantSagasFactory(...params),
    ...shoppingAssistantSagasFactory(...params),
    ...footerSagasFactory(...params),
  ];
}

function generateServerRootSaga(...params) {
  function* rootSaga() {
    yield all([
      ...generateUniversalSagas(...params),
    ]);
  }

  return rootSaga;
}

function generateClientRootSaga(...params) {
  function* rootSaga() {
    yield all([
      ...generateUniversalSagas(...params),
      ...analyticsSagasFactory(...params),
    ]);
  }

  return rootSaga;
}

export {
  generateInitialReduxStoreState,
  generateRootReducer,
  generateClientRootSaga,
  generateServerRootSaga,
};
